import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays, subMonths, subYears, startOfYear } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  DollarSign, Users, Calendar as CalendarIcon, TrendingUp,
  Download, FileSpreadsheet, Loader2, X, BarChart3, PieChartIcon, FileText, ChevronDown,
  Package, Receipt, ShoppingCart, EyeOff
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#14b8a6', '#8b5cf6', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#10b981', '#6366f1'];

const ReportsDashboard = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [ecardData, setEcardData] = useState(null);
  const [dateRange, setDateRange] = useState('30days');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [exemptToggles, setExemptToggles] = useState({}); // Track local exempt state for UI

  // Handle toggling reporting exempt
  const handleToggleExempt = async (itemType, itemId, currentExempt) => {
    const newExempt = !currentExempt;
    
    // Optimistic UI update
    setExemptToggles(prev => ({ ...prev, [`${itemType}-${itemId}`]: newExempt }));
    
    try {
      await axios.post(`${API}/admin/reports/toggle-exempt`, {
        item_type: itemType,
        item_id: itemId,
        exempt: newExempt
      });
      toast.success(`Item ${newExempt ? 'excluded from' : 'included in'} reporting`);
      // Refresh data to get updated totals
      fetchReportData();
    } catch (error) {
      // Revert on error
      setExemptToggles(prev => ({ ...prev, [`${itemType}-${itemId}`]: currentExempt }));
      toast.error('Failed to update exempt status');
      console.error('Error toggling exempt:', error);
    }
  };

  // Get current exempt state (from toggle or original data)
  const getExemptState = (itemType, itemId, originalExempt) => {
    const key = `${itemType}-${itemId}`;
    return key in exemptToggles ? exemptToggles[key] : originalExempt;
  };

  // Reference to fetchReports for refreshing after exempt toggle
  const fetchReportData = () => fetchReports();

  const getDateRange = () => {
    const now = new Date();
    let start, end = now; // Default end to TODAY for accurate sales reporting
    
    switch (dateRange) {
      case '7days':
        start = subDays(now, 7);
        end = now;
        break;
      case '30days':
        start = subDays(now, 30);
        end = now;
        break;
      case '90days':
        start = subDays(now, 90);
        end = now;
        break;
      case 'thisYear':
        start = startOfYear(now);
        end = now;
        break;
      case 'lastYear':
        start = subYears(startOfYear(now), 1);
        end = subDays(startOfYear(now), 1);
        break;
      case 'allTime':
        start = new Date(2020, 0, 1);
        end = now;
        break;
      case 'custom':
        start = customStart || subDays(now, 30);
        end = customEnd || now;
        break;
      default:
        start = subDays(now, 30);
        end = now;
    }
    
    return { start, end };
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const params = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: end.toISOString()
      });
      
      // Fetch both class registration and instructor eCard data
      const [summaryResponse, ecardResponse] = await Promise.all([
        axios.get(`${API}/admin/reports/summary?${params}`),
        axios.get(`${API}/admin/reports/instructor-ecards?${params}`)
      ]);
      
      setData(summaryResponse.data);
      setEcardData(ecardResponse.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchReports();
    }
  }, [open, dateRange, customStart, customEnd]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const { start, end } = getDateRange();
      const params = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: end.toISOString()
      });
      
      const response = await axios.get(`${API}/admin/reports/export/csv?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookings_report_${format(start, 'yyyyMMdd')}_${format(end, 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = (reportType = 'all') => {
    const { start, end } = getDateRange();
    const dateRangeText = `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
    
    const printWindow = window.open('', '_blank');
    
    let content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Reports - Biggest Little CPR</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              max-width: 1000px;
              margin: 0 auto;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #14b8a6;
              padding-bottom: 20px;
            }
            .header h1 {
              margin: 0 0 10px 0;
              font-size: 28px;
              color: #0f172a;
            }
            .header .date-range {
              color: #64748b;
              font-size: 14px;
            }
            .section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .section h2 {
              font-size: 18px;
              color: #0f172a;
              border-bottom: 1px solid #e2e8f0;
              padding-bottom: 10px;
              margin-bottom: 15px;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            .stat-card {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 15px;
              text-align: center;
            }
            .stat-card .label {
              font-size: 11px;
              color: #64748b;
              text-transform: uppercase;
              margin-bottom: 5px;
            }
            .stat-card .value {
              font-size: 24px;
              font-weight: bold;
              color: #0f172a;
            }
            .stat-card .subtitle {
              font-size: 11px;
              color: #94a3b8;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 15px;
            }
            th, td {
              border: 1px solid #e2e8f0;
              padding: 10px;
              text-align: left;
            }
            th {
              background: #f1f5f9;
              font-weight: 600;
              font-size: 12px;
              text-transform: uppercase;
            }
            td {
              font-size: 13px;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              color: #94a3b8;
              font-size: 11px;
              border-top: 1px solid #e2e8f0;
              padding-top: 20px;
            }
            @media print {
              body { padding: 20px; }
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📊 ${reportType === 'all' ? 'Complete Reports' : reportType === 'instructor-ecards' ? 'Instructor eCard Reports' : reportType.charAt(0).toUpperCase() + reportType.slice(1) + ' Report'}</h1>
            <div class="date-range">Biggest Little CPR • ${dateRangeText}</div>
          </div>
    `;
    
    // Add relevant sections based on reportType
    if (data && (reportType === 'all' || reportType === 'overview' || reportType === 'revenue')) {
      content += `
        <div class="section">
          <h2>💰 Revenue Summary</h2>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="label">Total Revenue</div>
              <div class="value">$${data.revenue?.total?.toLocaleString() || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Classes Held</div>
              <div class="value">${data.classes?.total || 0}</div>
              <div class="subtitle">${data.classes?.avg_students || 0} avg students</div>
            </div>
            <div class="stat-card">
              <div class="label">Students</div>
              <div class="value">${data.students?.total_period || 0}</div>
              <div class="subtitle">${data.students?.total_all_time || 0} all time</div>
            </div>
            <div class="stat-card">
              <div class="label">Bookings</div>
              <div class="value">${data.bookings?.total || 0}</div>
              <div class="subtitle">${data.bookings?.approval_rate || 0}% approved</div>
            </div>
          </div>
          ${data.revenue?.by_class_type?.length > 0 ? `
            <h3>Revenue by Class Type</h3>
            <table>
              <thead><tr><th>Class Type</th><th>Revenue</th></tr></thead>
              <tbody>
                ${data.revenue.by_class_type.map(item => `<tr><td>${item.name}</td><td>$${item.revenue?.toLocaleString() || 0}</td></tr>`).join('')}
              </tbody>
            </table>
          ` : ''}
        </div>
      `;
    }
    
    if (data && (reportType === 'all' || reportType === 'classes')) {
      content += `
        <div class="section">
          <h2>📅 Classes Summary</h2>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="label">Total Classes</div>
              <div class="value">${data.classes?.total || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Avg Students/Class</div>
              <div class="value">${data.classes?.avg_students || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Fill Rate</div>
              <div class="value">${data.classes?.fill_rate || 0}%</div>
            </div>
            <div class="stat-card">
              <div class="label">Most Popular</div>
              <div class="value" style="font-size: 14px;">${data.classes?.by_type?.[0]?.name?.substring(0, 15) || 'N/A'}</div>
            </div>
          </div>
          ${data.classes?.by_type?.length > 0 ? `
            <h3>Classes by Type</h3>
            <table>
              <thead><tr><th>Class Type</th><th>Count</th></tr></thead>
              <tbody>
                ${data.classes.by_type.map(item => `<tr><td>${item.name}</td><td>${item.count}</td></tr>`).join('')}
              </tbody>
            </table>
          ` : ''}
        </div>
      `;
    }
    
    if (data && (reportType === 'all' || reportType === 'students')) {
      content += `
        <div class="section">
          <h2>👥 Students Summary</h2>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="label">All-Time Students</div>
              <div class="value">${data.students?.total_all_time || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Period Students</div>
              <div class="value">${data.students?.total_period || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">New Students</div>
              <div class="value">${data.students?.new || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Returning Students</div>
              <div class="value">${data.students?.returning || 0}</div>
            </div>
          </div>
        </div>
      `;
    }
    
    if (data && (reportType === 'all' || reportType === 'bookings')) {
      content += `
        <div class="section">
          <h2>📋 Bookings Summary</h2>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="label">Total Bookings</div>
              <div class="value">${data.bookings?.total || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Approved</div>
              <div class="value" style="color: #10b981;">${data.bookings?.approved || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Rejected</div>
              <div class="value" style="color: #ef4444;">${data.bookings?.rejected || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Pending</div>
              <div class="value" style="color: #f59e0b;">${data.bookings?.pending || 0}</div>
            </div>
          </div>
          <div class="stats-grid" style="grid-template-columns: repeat(2, 1fr);">
            <div class="stat-card">
              <div class="label">Public Classes</div>
              <div class="value">${data.bookings?.public || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Private Classes</div>
              <div class="value">${data.bookings?.private || 0}</div>
            </div>
          </div>
          <p style="margin-top: 15px;"><strong>Approval Rate:</strong> ${data.bookings?.approval_rate || 0}% | <strong>Rejection Rate:</strong> ${data.bookings?.rejection_rate || 0}%</p>
        </div>
      `;
    }
    
    if (ecardData && (reportType === 'all' || reportType === 'instructor-ecards')) {
      content += `
        <div class="section">
          <h2>🎓 Instructor eCard Orders</h2>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="label">eCard Revenue</div>
              <div class="value">$${ecardData.summary?.total_revenue?.toLocaleString() || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Total Orders</div>
              <div class="value">${ecardData.summary?.total_orders || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Cards Ordered</div>
              <div class="value">${ecardData.summary?.total_cards_ordered || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Avg Order Value</div>
              <div class="value">$${ecardData.summary?.avg_order_value?.toFixed(2) || 0}</div>
            </div>
          </div>
          <h3>Order Status</h3>
          <div class="stats-grid">
            <div class="stat-card">
              <div class="label">Pending</div>
              <div class="value" style="color: #f59e0b;">${ecardData.orders_by_status?.pending || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Ordered</div>
              <div class="value" style="color: #3b82f6;">${ecardData.orders_by_status?.ordered || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Fulfilled</div>
              <div class="value" style="color: #10b981;">${ecardData.orders_by_status?.fulfilled || 0}</div>
            </div>
            <div class="stat-card">
              <div class="label">Cancelled</div>
              <div class="value" style="color: #ef4444;">${ecardData.orders_by_status?.cancelled || 0}</div>
            </div>
          </div>
          ${ecardData.revenue?.by_product?.length > 0 ? `
            <h3>Revenue by Product</h3>
            <table>
              <thead><tr><th>Product</th><th>Revenue</th></tr></thead>
              <tbody>
                ${ecardData.revenue.by_product.map(item => `<tr><td>${item.name}</td><td>$${item.revenue?.toLocaleString() || 0}</td></tr>`).join('')}
              </tbody>
            </table>
          ` : ''}
        </div>
      `;
    }
    
    content += `
          <div class="footer">
            <p>Biggest Little CPR - Reports Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    
    // Slight delay to ensure content is rendered
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'teal' }) => (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-white uppercase tracking-wide">{title}</p>
            <p className={`text-2xl font-bold text-${color}-400 mt-1`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg bg-${color}-900/30`}>
            <Icon className={`h-6 w-6 text-${color}-400`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader className="border-b border-slate-700 pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-teal-400" />
              Reports Dashboard
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:text-slate-200">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Date Range Filters */}
        <div className="flex flex-wrap items-center gap-2 py-4 border-b border-slate-800">
          <span className="text-sm text-white">Period:</span>
          {[
            { key: '7days', label: '7 Days' },
            { key: '30days', label: '30 Days' },
            { key: '90days', label: '90 Days' },
            { key: 'thisYear', label: 'This Year' },
            { key: 'lastYear', label: 'Last Year' },
            { key: 'allTime', label: 'All Time' },
          ].map(({ key, label }) => (
            <Button
              key={key}
              variant={dateRange === key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateRange(key)}
              className={dateRange === key 
                ? 'bg-teal-600 hover:bg-teal-500 text-white' 
                : 'border-teal-600 text-white hover:bg-slate-700'}
            >
              {label}
            </Button>
          ))}
          
          {/* Custom Date Range */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={dateRange === 'custom' ? 'default' : 'outline'}
                size="sm"
                className={dateRange === 'custom' 
                  ? 'bg-teal-600 hover:bg-teal-500 text-white' 
                  : 'border-teal-600 text-white hover:bg-slate-700'}
              >
                <CalendarIcon className="h-4 w-4 mr-2" />
                Custom
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700" align="start">
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-xs text-white mb-2">Start Date</p>
                  <Calendar
                    mode="single"
                    selected={customStart}
                    onSelect={(date) => { setCustomStart(date); setDateRange('custom'); }}
                    className="rounded-md border border-teal-600"
                  />
                </div>
                <div>
                  <p className="text-xs text-white mb-2">End Date</p>
                  <Calendar
                    mode="single"
                    selected={customEnd}
                    onSelect={(date) => { setCustomEnd(date); setDateRange('custom'); }}
                    className="rounded-md border border-teal-600"
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={exporting}
              className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
              data-testid="export-csv-btn"
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
              Export CSV
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-600 text-red-400 hover:bg-red-900/30"
                  data-testid="export-pdf-dropdown"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Export PDF
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-800 border-slate-700">
                <DropdownMenuItem 
                  onClick={() => handleExportPDF(activeTab)}
                  className="text-white hover:bg-slate-700 cursor-pointer"
                  data-testid="export-pdf-current"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Export Current Tab ({activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace('-', ' ')})
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleExportPDF('all')}
                  className="text-white hover:bg-slate-700 cursor-pointer"
                  data-testid="export-pdf-all"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export All Reports
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-teal-400 animate-spin" />
          </div>
        ) : data ? (
          <Tabs value={activeTab} className="mt-4" onValueChange={setActiveTab}>
            <TabsList className="bg-slate-800 border-slate-700 flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-teal-600" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="revenue" className="data-[state=active]:bg-teal-600" data-testid="tab-revenue">Revenue</TabsTrigger>
              <TabsTrigger value="bookings" className="data-[state=active]:bg-teal-600" data-testid="tab-bookings">Bookings</TabsTrigger>
              <TabsTrigger value="invoices" className="data-[state=active]:bg-blue-600" data-testid="tab-invoices">Invoices</TabsTrigger>
              <TabsTrigger value="rentals" className="data-[state=active]:bg-purple-600" data-testid="tab-rentals">Rentals</TabsTrigger>
              <TabsTrigger value="pos" className="data-[state=active]:bg-amber-600" data-testid="tab-pos">POS Sales</TabsTrigger>
              <TabsTrigger value="classes" className="data-[state=active]:bg-teal-600" data-testid="tab-classes">Classes</TabsTrigger>
              <TabsTrigger value="students" className="data-[state=active]:bg-teal-600" data-testid="tab-students">Students</TabsTrigger>
              <TabsTrigger value="instructor-ecards" className="data-[state=active]:bg-purple-600 whitespace-nowrap" data-testid="tab-instructor-ecards">Instructor eCards</TabsTrigger>
            </TabsList>

            {/* OVERVIEW TAB */}
            <TabsContent value="overview" className="space-y-6 mt-4">
              {/* Combined Revenue Summary */}
              <Card className="bg-gradient-to-r from-emerald-900/50 to-slate-800 border-emerald-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-emerald-300 flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Total Revenue: ${data.combined_revenue?.total?.toLocaleString() || data.revenue.total.toLocaleString()}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                      <p className="text-xs text-slate-400">Class Bookings</p>
                      <p className="text-lg font-bold text-teal-400">${data.combined_revenue?.booking_revenue?.toLocaleString() || data.revenue.total.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">{data.combined_revenue?.breakdown_summary?.bookings?.count || data.bookings.approved} bookings</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                      <p className="text-xs text-slate-400">POS Sales</p>
                      <p className="text-lg font-bold text-amber-400">${data.pos_sales?.total?.toLocaleString() || 0}</p>
                      <p className="text-xs text-slate-500">{data.pos_sales?.order_count || 0} orders</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                      <p className="text-xs text-slate-400">Invoices</p>
                      <p className="text-lg font-bold text-blue-400">${data.invoice_sales?.invoice_total?.toLocaleString() || 0}</p>
                      <p className="text-xs text-slate-500">{data.invoice_sales?.invoice_count || 0} paid</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                      <p className="text-xs text-slate-400">Quotes (Converted)</p>
                      <p className="text-lg font-bold text-purple-400">${data.invoice_sales?.quote_total?.toLocaleString() || 0}</p>
                      <p className="text-xs text-slate-500">{data.invoice_sales?.quote_count || 0} paid</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                      <p className="text-xs text-slate-400">Rentals</p>
                      <p className="text-lg font-bold text-pink-400">${data.rental_sales?.total?.toLocaleString() || 0}</p>
                      <p className="text-xs text-slate-500">{data.rental_sales?.order_count || 0} orders</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard 
                  title="Booking Revenue" 
                  value={`$${data.revenue.total.toLocaleString()}`}
                  icon={DollarSign}
                  color="emerald"
                />
                <StatCard 
                  title="Classes Held" 
                  value={data.classes.total}
                  subtitle={`${data.classes.avg_students} avg students`}
                  icon={CalendarIcon}
                  color="blue"
                />
                <StatCard 
                  title="Students (Period)" 
                  value={data.students.total_period}
                  subtitle={`${data.students.total_all_time} all time`}
                  icon={Users}
                  color="purple"
                />
                <StatCard 
                  title="Bookings" 
                  value={data.bookings.total}
                  subtitle={`${data.bookings.approval_rate}% approved`}
                  icon={TrendingUp}
                  color="amber"
                />
              </div>

              {/* Charts Row */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Revenue Trend */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-white">Revenue Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={data.revenue.by_period}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                          labelStyle={{ color: '#e2e8f0' }}
                        />
                        <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Revenue by Class Type */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-white">Revenue by Class Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={data.revenue.by_class_type}
                          dataKey="revenue"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                          label={({ name, percent }) => `${name.substring(0, 15)}${name.length > 15 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={{ stroke: '#64748b' }}
                        >
                          {data.revenue.by_class_type.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                          formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* REVENUE TAB */}
            <TabsContent value="revenue" className="space-y-6 mt-4">
              {/* Combined Revenue Header */}
              <Card className="bg-gradient-to-r from-emerald-900/50 to-slate-800 border-emerald-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Combined Total Revenue</p>
                      <p className="text-3xl font-bold text-emerald-400">
                        ${data.combined_revenue?.total?.toLocaleString() || data.revenue.total.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-teal-400">Bookings: ${data.combined_revenue?.booking_revenue?.toLocaleString() || data.revenue.total.toLocaleString()}</p>
                      <p className="text-amber-400">POS: ${data.pos_sales?.total?.toLocaleString() || 0}</p>
                      <p className="text-blue-400">Invoices/Quotes: ${data.invoice_sales?.combined?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard title="Booking Revenue" value={`$${data.revenue.total.toLocaleString()}`} icon={DollarSign} color="teal" />
                <StatCard title="POS Sales" value={`$${data.pos_sales?.total?.toLocaleString() || 0}`} subtitle={`${data.pos_sales?.order_count || 0} orders`} icon={DollarSign} color="amber" />
                <StatCard title="Invoice Revenue" value={`$${data.invoice_sales?.invoice_total?.toLocaleString() || 0}`} subtitle={`${data.invoice_sales?.invoice_count || 0} invoices`} icon={DollarSign} color="blue" />
                <StatCard title="Quote Revenue" value={`$${data.invoice_sales?.quote_total?.toLocaleString() || 0}`} subtitle={`${data.invoice_sales?.quote_count || 0} quotes`} icon={DollarSign} color="purple" />
              </div>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white">Monthly Booking Revenue</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.revenue.by_period}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" tick={{ fill: '#94a3b8' }} />
                      <YAxis tick={{ fill: '#94a3b8' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                      <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Booking Breakdown Table */}
              {data.booking_breakdown && data.booking_breakdown.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="h-4 w-4 text-teal-400" />
                      Booking Revenue Breakdown ({data.booking_breakdown.length} recent)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="text-slate-400 border-b border-slate-700 sticky top-0 bg-slate-800">
                          <tr>
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-left py-2 px-2">Class</th>
                            <th className="text-left py-2 px-2">Customer</th>
                            <th className="text-center py-2 px-2">Students</th>
                            <th className="text-right py-2 px-2">Amount</th>
                            <th className="text-center py-2 px-2">Include</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-300">
                          {data.booking_breakdown.map((b, idx) => {
                            const isExempt = getExemptState('booking', b.booking_id, b.reporting_exempt);
                            return (
                              <tr key={idx} className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${isExempt ? 'bg-red-900/20' : ''}`}>
                                <td className={`py-2 px-2 ${isExempt ? 'text-red-400' : ''}`}>{b.date}</td>
                                <td className={`py-2 px-2 truncate max-w-[150px] ${isExempt ? 'text-red-400 line-through' : ''}`} title={b.class_name}>{b.class_name}</td>
                                <td className={`py-2 px-2 truncate max-w-[120px] ${isExempt ? 'text-red-400' : ''}`}>{b.customer}</td>
                                <td className={`py-2 px-2 text-center ${isExempt ? 'text-red-400' : ''}`}>{b.students}</td>
                                <td className={`py-2 px-2 text-right font-medium ${isExempt ? 'text-red-400 line-through' : 'text-teal-400'}`}>${b.amount.toLocaleString()}</td>
                                <td className="py-2 px-2 text-center">
                                  <Checkbox
                                    checked={!isExempt}
                                    onCheckedChange={() => handleToggleExempt('booking', b.booking_id, isExempt)}
                                    className="border-slate-500 data-[state=checked]:bg-teal-600"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* POS Sales Breakdown Table */}
              {data.pos_sales?.breakdown && data.pos_sales.breakdown.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="h-4 w-4 text-amber-400" />
                      POS Sales Breakdown ({data.pos_sales.breakdown.length} recent)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="text-slate-400 border-b border-slate-700 sticky top-0 bg-slate-800">
                          <tr>
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-left py-2 px-2">Order ID</th>
                            <th className="text-left py-2 px-2">Customer</th>
                            <th className="text-center py-2 px-2">Items</th>
                            <th className="text-center py-2 px-2">Payment</th>
                            <th className="text-right py-2 px-2">Total</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-300">
                          {data.pos_sales.breakdown.map((o, idx) => (
                            <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                              <td className="py-2 px-2">{o.date}</td>
                              <td className="py-2 px-2 font-mono text-xs">{o.order_id}</td>
                              <td className="py-2 px-2">{o.customer}</td>
                              <td className="py-2 px-2 text-center">{o.items_count}</td>
                              <td className="py-2 px-2 text-center">
                                <span className={`px-2 py-0.5 rounded text-xs ${o.payment_method === 'Stripe' ? 'bg-blue-900/50 text-blue-300' : o.payment_method === 'Cash' ? 'bg-green-900/50 text-green-300' : 'bg-slate-700 text-slate-300'}`}>
                                  {o.payment_method}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-right text-amber-400 font-medium">${o.total.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Invoice Sales Breakdown Table */}
              {data.invoice_sales?.breakdown && data.invoice_sales.breakdown.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-400" />
                      Invoice/Quote Payments Breakdown ({data.invoice_sales.breakdown.length} recent)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="text-slate-400 border-b border-slate-700 sticky top-0 bg-slate-800">
                          <tr>
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-left py-2 px-2">Invoice #</th>
                            <th className="text-left py-2 px-2">Type</th>
                            <th className="text-left py-2 px-2">Customer</th>
                            <th className="text-right py-2 px-2">Total</th>
                            <th className="text-right py-2 px-2">Paid</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-300">
                          {data.invoice_sales.breakdown.map((inv, idx) => (
                            <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                              <td className="py-2 px-2">{inv.date}</td>
                              <td className="py-2 px-2 font-mono text-xs">{inv.invoice_id}</td>
                              <td className="py-2 px-2">
                                <span className={`px-2 py-0.5 rounded text-xs ${inv.type === 'Quote' ? 'bg-purple-900/50 text-purple-300' : 'bg-blue-900/50 text-blue-300'}`}>
                                  {inv.type}
                                </span>
                              </td>
                              <td className="py-2 px-2 truncate max-w-[120px]">{inv.customer}</td>
                              <td className="py-2 px-2 text-right text-slate-400">${inv.total.toLocaleString()}</td>
                              <td className="py-2 px-2 text-right text-blue-400 font-medium">${inv.amount_paid.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white">Revenue by Class Type</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={data.revenue.by_class_type} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis type="number" tick={{ fill: '#94a3b8' }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={120} />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                        <Bar dataKey="revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white">Payment Methods (Bookings)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={data.revenue.by_payment_method}
                          dataKey="revenue"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {data.revenue.by_payment_method.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} formatter={(v) => `$${v.toLocaleString()}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* CLASSES TAB */}
            <TabsContent value="classes" className="space-y-6 mt-4">
              <div className="grid grid-cols-4 gap-4">
                <StatCard title="Total Classes" value={data.classes.total} icon={CalendarIcon} color="blue" />
                <StatCard title="Avg Students/Class" value={data.classes.avg_students} icon={Users} color="purple" />
                <StatCard title="Fill Rate" value={`${data.classes.fill_rate}%`} icon={TrendingUp} color="amber" />
                <StatCard 
                  title="Most Popular" 
                  value={data.classes.by_type[0]?.name?.substring(0, 12) || 'N/A'} 
                  subtitle={`${data.classes.by_type[0]?.count || 0} classes`}
                  icon={BarChart3} 
                  color="teal" 
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white">Classes Over Time</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={data.classes.by_period}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" tick={{ fill: '#94a3b8' }} />
                        <YAxis tick={{ fill: '#94a3b8' }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                        <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white">Classes by Type</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={data.classes.by_type}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                        <YAxis tick={{ fill: '#94a3b8' }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                        <Bar dataKey="count" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* STUDENTS TAB */}
            <TabsContent value="students" className="space-y-6 mt-4">
              <div className="grid grid-cols-4 gap-4">
                <StatCard title="All-Time Students" value={data.students.total_all_time} icon={Users} color="purple" />
                <StatCard title="Period Students" value={data.students.total_period} icon={Users} color="blue" />
                <StatCard title="New Students" value={data.students.new} subtitle="First time in period" icon={TrendingUp} color="emerald" />
                <StatCard title="Returning Students" value={data.students.returning} subtitle="Trained before" icon={Users} color="amber" />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white">Student Growth Over Time</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={data.students.growth}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" tick={{ fill: '#94a3b8' }} />
                        <YAxis tick={{ fill: '#94a3b8' }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                        <Line type="monotone" dataKey="count" stroke="#8b5cf6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white">New vs Returning Students</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'New Students', value: data.students.new },
                            { name: 'Returning', value: data.students.returning }
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          <Cell fill="#10b981" />
                          <Cell fill="#f59e0b" />
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* BOOKINGS TAB */}
            <TabsContent value="bookings" className="space-y-6 mt-4">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <StatCard title="Total Bookings" value={data.bookings.total} icon={CalendarIcon} color="blue" />
                <StatCard title="Approved" value={data.bookings.approved} icon={TrendingUp} color="emerald" />
                <StatCard title="Rejected" value={data.bookings.rejected} icon={X} color="red" />
                <StatCard title="Pending" value={data.bookings.pending} icon={Loader2} color="amber" />
                <StatCard title="Public Classes" value={data.bookings.public} icon={Users} color="purple" />
                <StatCard title="Private Classes" value={data.bookings.private} icon={Users} color="teal" />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white">Bookings Over Time</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={data.bookings.by_period}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" tick={{ fill: '#94a3b8' }} />
                        <YAxis tick={{ fill: '#94a3b8' }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                        <Legend />
                        <Bar dataKey="approved" fill="#10b981" name="Approved" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="rejected" fill="#ef4444" name="Rejected" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader><CardTitle className="text-white">Booking Status Distribution</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Approved', value: data.bookings.approved },
                            { name: 'Rejected', value: data.bookings.rejected },
                            { name: 'Pending', value: data.bookings.pending }
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name, value }) => value > 0 ? `${name} (${value})` : ''}
                        >
                          <Cell fill="#10b981" />
                          <Cell fill="#ef4444" />
                          <Cell fill="#f59e0b" />
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white">Conversion Rates</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <p className="text-white text-sm mb-2">Approval Rate</p>
                      <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${data.bookings.approval_rate}%` }}
                        />
                      </div>
                      <p className="text-emerald-400 text-lg font-bold mt-1">{data.bookings.approval_rate}%</p>
                    </div>
                    <div>
                      <p className="text-white text-sm mb-2">Rejection Rate</p>
                      <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-red-500 rounded-full transition-all"
                          style={{ width: `${data.bookings.rejection_rate}%` }}
                        />
                      </div>
                      <p className="text-red-400 text-lg font-bold mt-1">{data.bookings.rejection_rate}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* INVOICES TAB */}
            <TabsContent value="invoices" className="space-y-6 mt-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-blue-400 flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Invoice & Quote Breakdown
                  </CardTitle>
                  <p className="text-sm text-slate-400">
                    Total: ${((data.invoice_sales?.invoice_total || 0) + (data.invoice_sales?.quote_total || 0)).toLocaleString()} 
                    ({data.invoice_sales?.invoice_count || 0} invoices + {data.invoice_sales?.quote_count || 0} quotes)
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {data.invoice_sales?.breakdown?.length > 0 ? (
                      data.invoice_sales.breakdown.map((item, idx) => {
                        const isExempt = getExemptState('invoice', item.invoice_id, item.reporting_exempt);
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isExempt ? 'bg-red-900/20 border-red-700/50' : 'bg-slate-700/50 border-slate-600'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={!isExempt}
                                onCheckedChange={() => handleToggleExempt('invoice', item.invoice_id, isExempt)}
                                className="border-slate-500 data-[state=checked]:bg-blue-600"
                              />
                              <div>
                                <p className={`font-medium ${isExempt ? 'text-red-400 line-through' : 'text-white'}`}>
                                  {item.invoice_id} - {item.type}
                                </p>
                                <p className={`text-sm ${isExempt ? 'text-red-400/70' : 'text-slate-400'}`}>
                                  {item.customer} • {item.date}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${isExempt ? 'text-red-400 line-through' : 'text-blue-400'}`}>
                                ${item.amount_paid?.toLocaleString()}
                              </p>
                              {isExempt && (
                                <p className="text-xs text-red-400 flex items-center gap-1">
                                  <EyeOff className="h-3 w-3" /> Exempt
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-slate-400 text-center py-4">No paid invoices in this period</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* RENTALS TAB */}
            <TabsContent value="rentals" className="space-y-6 mt-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-pink-400 flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Equipment Rental Breakdown
                  </CardTitle>
                  <p className="text-sm text-slate-400">
                    Total Revenue: ${data.rental_sales?.total?.toLocaleString() || 0} ({data.rental_sales?.order_count || 0} orders)
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {data.rental_sales?.breakdown?.length > 0 ? (
                      data.rental_sales.breakdown.map((item, idx) => {
                        const isExempt = getExemptState('rental_order', item.order_id, item.reporting_exempt);
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isExempt ? 'bg-red-900/20 border-red-700/50' : 'bg-slate-700/50 border-slate-600'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={!isExempt}
                                onCheckedChange={() => handleToggleExempt('rental_order', item.order_id, isExempt)}
                                className="border-slate-500 data-[state=checked]:bg-pink-600"
                              />
                              <div>
                                <p className={`font-medium ${isExempt ? 'text-red-400 line-through' : 'text-white'}`}>
                                  {item.order_id}
                                </p>
                                <p className={`text-sm ${isExempt ? 'text-red-400/70' : 'text-slate-400'}`}>
                                  {item.customer} • {item.date} • {item.items_count} item(s)
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${isExempt ? 'text-red-400 line-through' : 'text-pink-400'}`}>
                                ${item.revenue?.toLocaleString()}
                              </p>
                              <p className="text-xs text-slate-500">Deposit: ${item.deposit?.toLocaleString()}</p>
                              {isExempt && (
                                <p className="text-xs text-red-400 flex items-center gap-1">
                                  <EyeOff className="h-3 w-3" /> Exempt
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-slate-400 text-center py-4">No completed rentals in this period</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* POS SALES TAB */}
            <TabsContent value="pos" className="space-y-6 mt-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-amber-400 flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    POS Sales Breakdown
                  </CardTitle>
                  <p className="text-sm text-slate-400">
                    Total: ${data.pos_sales?.total?.toLocaleString() || 0} ({data.pos_sales?.order_count || 0} orders)
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {data.pos_sales?.breakdown?.length > 0 ? (
                      data.pos_sales.breakdown.map((item, idx) => {
                        const isExempt = getExemptState('pos_order', item.order_id, item.reporting_exempt);
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isExempt ? 'bg-red-900/20 border-red-700/50' : 'bg-slate-700/50 border-slate-600'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={!isExempt}
                                onCheckedChange={() => handleToggleExempt('pos_order', item.order_id, isExempt)}
                                className="border-slate-500 data-[state=checked]:bg-amber-600"
                              />
                              <div>
                                <p className={`font-medium ${isExempt ? 'text-red-400 line-through' : 'text-white'}`}>
                                  {item.order_id}
                                </p>
                                <p className={`text-sm ${isExempt ? 'text-red-400/70' : 'text-slate-400'}`}>
                                  {item.customer} • {item.date} • {item.payment_method}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${isExempt ? 'text-red-400 line-through' : 'text-amber-400'}`}>
                                ${item.total?.toLocaleString()}
                              </p>
                              {isExempt && (
                                <p className="text-xs text-red-400 flex items-center gap-1">
                                  <EyeOff className="h-3 w-3" /> Exempt
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-slate-400 text-center py-4">No POS orders in this period</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* INSTRUCTOR ECARDS TAB */}
            <TabsContent value="instructor-ecards" className="space-y-6 mt-4">
              {ecardData ? (
                <>
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <StatCard 
                      title="eCard Revenue" 
                      value={`$${ecardData.summary?.total_revenue?.toLocaleString() || 0}`}
                      icon={DollarSign}
                      color="purple"
                    />
                    <StatCard 
                      title="Total Orders" 
                      value={ecardData.summary?.total_orders || 0}
                      icon={BarChart3}
                      color="blue"
                    />
                    <StatCard 
                      title="Cards Ordered" 
                      value={ecardData.summary?.total_cards_ordered || 0}
                      icon={TrendingUp}
                      color="teal"
                    />
                    <StatCard 
                      title="Unique Instructors" 
                      value={ecardData.summary?.unique_instructors || 0}
                      icon={Users}
                      color="amber"
                    />
                    <StatCard 
                      title="Avg Order Value" 
                      value={`$${ecardData.summary?.avg_order_value?.toFixed(2) || 0}`}
                      icon={DollarSign}
                      color="emerald"
                    />
                  </div>

                  {/* Order Status Breakdown */}
                  <div className="grid grid-cols-4 gap-4">
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-white uppercase">Pending</p>
                        <p className="text-2xl font-bold text-yellow-400">{ecardData.orders_by_status?.pending || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-white uppercase">Ordered</p>
                        <p className="text-2xl font-bold text-blue-400">{ecardData.orders_by_status?.ordered || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-white uppercase">Fulfilled</p>
                        <p className="text-2xl font-bold text-emerald-400">{ecardData.orders_by_status?.fulfilled || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-white uppercase">Cancelled</p>
                        <p className="text-2xl font-bold text-red-400">{ecardData.orders_by_status?.cancelled || 0}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Charts */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardHeader><CardTitle className="text-white">eCard Revenue Over Time</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={ecardData.revenue?.by_period || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="month" tick={{ fill: '#94a3b8' }} />
                            <YAxis tick={{ fill: '#94a3b8' }} />
                            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                            <Bar dataKey="revenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardHeader><CardTitle className="text-white">Revenue by Product</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={ecardData.revenue?.by_product || []}
                              dataKey="revenue"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label={({ name, percent }) => `${name?.substring(0, 12) || ''}${name?.length > 12 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                            >
                              {(ecardData.revenue?.by_product || []).map((entry, index) => (
                                <Cell key={index} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} formatter={(v) => `$${v?.toLocaleString() || 0}`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardHeader><CardTitle className="text-white">Cards Ordered by Product</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={ecardData.cards?.by_product || []} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis type="number" tick={{ fill: '#94a3b8' }} />
                            <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={120} />
                            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                            <Bar dataKey="cards" fill="#14b8a6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardHeader><CardTitle className="text-white">Orders Over Time</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={ecardData.orders?.by_period || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="month" tick={{ fill: '#94a3b8' }} />
                            <YAxis tick={{ fill: '#94a3b8' }} />
                            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }} />
                            <Legend />
                            <Bar dataKey="fulfilled" fill="#10b981" name="Fulfilled" stackId="a" />
                            <Bar dataKey="ordered" fill="#3b82f6" name="Ordered" stackId="a" />
                            <Bar dataKey="pending" fill="#f59e0b" name="Pending" stackId="a" />
                            <Bar dataKey="cancelled" fill="#ef4444" name="Cancelled" stackId="a" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>
                </>
              ) : (
                <div className="text-center py-10 text-white">
                  No instructor eCard data available
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="text-center py-20 text-white">
            No data available
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportsDashboard;
